/**
 * Admin System Storage Tab Component
 *
 * Displays disk usage and the backend log files, and provides manual cleanup
 * actions: download or delete individual log files, delete a selection, or
 * delete all files older than a chosen number of days.
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import {
  getStorageOverview,
  getStorageLogs,
  deleteLogFile,
  deleteLogFilesOlderThan,
  getLogFileDownloadUrl,
  type StorageOverview,
  type LogsListResponse,
} from "@app/services/adminSystemStorageApi";
import { logger } from "@app/services/logger";
import "./admin-system-storage.css";

const OVERVIEW_QUERY_KEY = ["admin", "storage", "overview"];
const LOGS_QUERY_KEY = ["admin", "storage", "logs"];

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

export function AdminSystemStorageTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [olderThanDays, setOlderThanDays] = useState(7);
  const [busy, setBusy] = useState(false);

  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
  } = useQuery<StorageOverview>({
    queryKey: OVERVIEW_QUERY_KEY,
    queryFn: getStorageOverview,
    refetchInterval: 30000,
    refetchOnWindowFocus: false,
    staleTime: 10000,
    retry: 2,
  });

  const {
    data: logs,
    isLoading: logsLoading,
    isError: logsError,
  } = useQuery<LogsListResponse>({
    queryKey: LOGS_QUERY_KEY,
    queryFn: getStorageLogs,
    refetchInterval: 30000,
    refetchOnWindowFocus: false,
    staleTime: 10000,
    retry: 2,
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: LOGS_QUERY_KEY }),
    ]);
  };

  const files = logs?.files ?? [];
  const allSelected = files.length > 0 && files.every((f) => selected.has(f.name) || f.is_active);
  const selectedCount = selected.size;

  const toggleSelect = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(files.filter((f) => !f.is_active).map((f) => f.name)));
    }
  };

  const handleDeleteOne = async (file: { name: string; size_bytes: number }) => {
    if (!window.confirm(`Delete log file "${file.name}" (${formatBytes(file.size_bytes)})?\n\nThis cannot be undone.`)) {
      return;
    }
    setBusy(true);
    try {
      const result = await deleteLogFile(file.name);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(file.name);
        return next;
      });
      await refresh();
      window.alert(result.message);
    } catch (error: any) {
      logger.error("Error deleting log file:", error);
      window.alert(`Failed to delete file: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedCount === 0) {
      window.alert("No files selected.");
      return;
    }
    if (!window.confirm(`Delete ${selectedCount} selected log file(s)?\n\nThis cannot be undone.`)) {
      return;
    }
    setBusy(true);
    const names = Array.from(selected);
    let success = 0;
    const errors: string[] = [];
    for (const name of names) {
      try {
        await deleteLogFile(name);
        success += 1;
      } catch (error: any) {
        logger.error(`Error deleting ${name}:`, error);
        errors.push(`${name}: ${error.message}`);
      }
    }
    setSelected(new Set());
    await refresh();
    setBusy(false);
    if (errors.length > 0) {
      window.alert(`Deleted ${success} of ${names.length} files.\n\nErrors:\n${errors.slice(0, 5).join("\n")}`);
    } else {
      window.alert(`Deleted ${success} file(s).`);
    }
  };

  const handleDeleteOlder = async () => {
    if (!window.confirm(`Delete all log files older than ${olderThanDays} days?\n\nThe active session file is always preserved.`)) {
      return;
    }
    setBusy(true);
    try {
      const result = await deleteLogFilesOlderThan(olderThanDays);
      setSelected(new Set());
      await refresh();
      window.alert(`${result.message}\nFreed: ${formatBytes(result.freed_bytes)}`);
    } catch (error: any) {
      logger.error("Error deleting old log files:", error);
      window.alert(`Failed to delete old log files: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (overviewLoading || logsLoading) {
    return (
      <div className="admin-storage-root">
        <Card title="Storage & Logs">
          <div className="admin-storage-loading">Loading storage data...</div>
        </Card>
      </div>
    );
  }

  if (overviewError || logsError || !overview || !logs) {
    return (
      <div className="admin-storage-root">
        <Card title="Storage & Logs">
          <div className="admin-storage-error">Failed to load storage data. The page will retry automatically.</div>
        </Card>
      </div>
    );
  }

  const usedPercent = Math.min(100, Math.max(0, overview.disk_percent_used));
  const barClass = usedPercent >= 90 ? "danger" : usedPercent >= 70 ? "warning" : "ok";

  return (
    <div className="admin-storage-root">
      <Card
        title="Storage & Logs"
        subtitle={`Last updated: ${new Date(overview.timestamp * 1000).toLocaleString()}`}
        actions={
          <button type="button" className="admin-storage-refresh" onClick={refresh} disabled={busy}>
            Refresh
          </button>
        }
      >
        {/* Disk usage overview */}
        <div className="admin-storage-overview">
          <div className="admin-storage-metric">
            <span className="admin-storage-metric__label">Total Disk</span>
            <span className="admin-storage-metric__value">{formatBytes(overview.disk_total_bytes)}</span>
          </div>
          <div className="admin-storage-metric">
            <span className="admin-storage-metric__label">Used</span>
            <span className="admin-storage-metric__value">{formatBytes(overview.disk_used_bytes)}</span>
          </div>
          <div className="admin-storage-metric">
            <span className="admin-storage-metric__label">Free</span>
            <span className="admin-storage-metric__value admin-storage-metric__value--ok">
              {formatBytes(overview.disk_free_bytes)}
            </span>
          </div>
          <div className="admin-storage-metric">
            <span className="admin-storage-metric__label">Logs Total</span>
            <span className="admin-storage-metric__value">{formatBytes(overview.logs_dir_total_bytes)}</span>
          </div>
          <div className="admin-storage-metric">
            <span className="admin-storage-metric__label">Log Files</span>
            <span className="admin-storage-metric__value">{overview.logs_dir_file_count}</span>
          </div>
        </div>

        <div className="admin-storage-bar">
          <div className={`admin-storage-bar__fill admin-storage-bar__fill--${barClass}`} style={{ width: `${usedPercent}%` }} />
        </div>
        <p className="admin-storage-bar__caption">
          {usedPercent.toFixed(1)}% of disk used ({formatBytes(overview.disk_used_bytes)} of {formatBytes(overview.disk_total_bytes)})
        </p>

        {/* Bulk actions */}
        <div className="admin-storage-actions">
          <div className="admin-storage-actions__group">
            <button
              type="button"
              className="admin-storage-btn admin-storage-btn--danger"
              onClick={handleDeleteSelected}
              disabled={selectedCount === 0 || busy}
            >
              Delete Selected ({selectedCount})
            </button>
          </div>
          <div className="admin-storage-actions__group">
            <label className="admin-storage-actions__label" htmlFor="older-than-days">
              Delete files older than
            </label>
            <input
              id="older-than-days"
              type="number"
              min={0}
              max={3650}
              value={olderThanDays}
              onChange={(e) => setOlderThanDays(Number(e.target.value))}
              className="admin-storage-actions__input"
            />
            <span className="admin-storage-actions__label">days</span>
            <button
              type="button"
              className="admin-storage-btn admin-storage-btn--danger"
              onClick={handleDeleteOlder}
              disabled={busy}
            >
              Clean Up
            </button>
          </div>
        </div>

        {/* Log file table */}
        <div className="admin-storage-files">
          <h3 className="admin-storage-files__title">Log Files ({files.length})</h3>
          {files.length === 0 ? (
            <div className="admin-storage-empty">No log files found.</div>
          ) : (
            <div className="admin-storage-table-wrap">
              <table className="admin-storage-table">
                <thead>
                  <tr>
                    <th className="admin-storage-table__check">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </th>
                    <th>File</th>
                    <th>Size</th>
                    <th>Modified</th>
                    <th>Status</th>
                    <th className="admin-storage-table__actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => {
                    const isSelected = selected.has(file.name);
                    return (
                      <tr key={file.name} className={`admin-storage-table__row${file.is_active ? " admin-storage-table__row--active" : ""}`}>
                        <td className="admin-storage-table__check">
                          {file.is_active ? (
                            <span title="Active file cannot be deleted" className="admin-storage-lock" aria-hidden="true">-</span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(file.name)}
                              aria-label={`Select ${file.name}`}
                            />
                          )}
                        </td>
                        <td className="admin-storage-table__name" title={file.name}>{file.name}</td>
                        <td>{file.size_human}</td>
                        <td>{formatDate(file.modified_at)}</td>
                        <td>
                          <span className={`admin-storage-badge admin-storage-badge--${file.is_active ? "active" : "inactive"}`}>
                            {file.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="admin-storage-table__actions">
                          <a
                            href={getLogFileDownloadUrl(file.name)}
                            className="admin-storage-btn admin-storage-btn--download"
                            download
                          >
                            Download
                          </a>
                          <button
                            type="button"
                            className="admin-storage-btn admin-storage-btn--danger"
                            onClick={() => handleDeleteOne(file)}
                            disabled={file.is_active || busy}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
